/**
 * SectionRenderer — renders any CMS section type using the homepage CSS classes.
 * Shared between public /:slug pages and admin preview.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, CheckCircle2, Star, ChevronDown } from "lucide-react";
import { htmlSafe } from "../lib/sanitize";

const BASE = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${BASE}${u}`;
  return u;
};
const DynIcon = ({ name, size = 20, strokeWidth = 1.8, ...rest }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp size={size} strokeWidth={strokeWidth} {...rest} />;
};

const scrollTo = (href) => {
  if (!href) return;
  if (href.startsWith("#")) document.getElementById(href.slice(1))?.scrollIntoView({ behavior: "smooth" });
  else window.location.href = href;
};

// ─── Individual section renderers ───

const Hero = ({ d }) => (
  <section className="hero-v2" style={{ minHeight: "auto", padding: "120px 24px 80px" }}>
    <div className="hero-grain" />
    {d.badge && <div className="hero-top"><div className="hero-badge"><span className="hero-badge-dot" />{d.badge}</div></div>}
    <div className="hero-display">
      <h1 className="hero-title">
        <span className="hero-word hero-word-1">{d.headline}</span>
        {d.subheadline && <span className="hero-word hero-word-2">{d.highlight_word && d.subheadline.includes(d.highlight_word) ? (<>{d.subheadline.split(d.highlight_word)[0]}<em>{d.highlight_word}</em>{d.subheadline.split(d.highlight_word).slice(1).join(d.highlight_word)}</>) : d.subheadline}</span>}
      </h1>
    </div>
    {d.subtitle && <div className="hero-meta"><p className="hero-sub">{d.subtitle}</p>
      <div className="hero-cta-row">
        {d.primary_cta?.label && <button onClick={() => scrollTo(d.primary_cta.href)} className="btn-pill-primary btn-pill-lg">{d.primary_cta.label} <ArrowRight size={18} strokeWidth={2.5} /></button>}
        {d.secondary_cta?.label && <button onClick={() => scrollTo(d.secondary_cta.href)} className="btn-pill-ghost btn-pill-lg">{d.secondary_cta.label}</button>}
      </div>
    </div>}
  </section>
);

const FeatureGrid = ({ d }) => (
  <section className="sec">
    <div className="sec-inner">
      <div className="sec-head">
        {d.eyebrow && <span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span>}
        {d.title && <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(d.title)} />}
        {d.description && <p className="sec-desc">{d.description}</p>}
      </div>
      <div className="services-v2-grid">
        {(d.cards || []).filter((c) => c.is_visible !== false).map((c) => (
          <div key={c.id} className="service-v2">
            <div className="service-v2-icon"><DynIcon name={c.icon || "Sparkles"} size={22} /></div>
            <h3 className="service-v2-title">{c.title}</h3>
            <p className="service-v2-desc">{c.description}</p>
            {c.pills?.length > 0 && <div className="service-v2-pills">{c.pills.map((p, i) => <span key={i} className="chip">{p}</span>)}</div>}
            <div className="service-v2-arrow"><ArrowUpRight size={18} /></div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const RichText = ({ d }) => (
  <section className="sec">
    <div className="sec-inner" style={{ maxWidth: 820 }}>
      {d.eyebrow && <div style={{ textAlign: "center", marginBottom: 24 }}><span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span></div>}
      {d.title && <h2 className="sec-title" style={{ textAlign: "center", marginBottom: 32 }} dangerouslySetInnerHTML={htmlSafe(d.title)} />}
      <div style={{ fontSize: "1.0625rem", lineHeight: 1.8, color: "var(--ink-2)" }} dangerouslySetInnerHTML={htmlSafe(d.html)} />
    </div>
  </section>
);

const CtaBanner = ({ d }) => (
  <section className="sec sec-mint">
    <div className="sec-inner" style={{ textAlign: "center" }}>
      {d.eyebrow && <span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span>}
      {d.title && <h2 className="sec-title" style={{ marginTop: 16 }} dangerouslySetInnerHTML={htmlSafe(d.title)} />}
      {d.description && <p className="sec-desc" style={{ marginTop: 16, marginBottom: 32 }}>{d.description}</p>}
      {d.cta?.label && <button onClick={() => scrollTo(d.cta.href)} className="btn-pill-primary btn-pill-lg" style={{ marginTop: 16 }}>{d.cta.label} <ArrowRight size={18} strokeWidth={2.5} /></button>}
    </div>
  </section>
);

const FAQItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid var(--line)", padding: "20px 0" }}>
      <button onClick={() => setOpen(!open)} style={{ background: "none", border: "none", width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Clash Display, sans-serif", fontSize: "1.125rem", fontWeight: 600, color: "var(--ink)", cursor: "pointer", padding: 0, textAlign: "left", letterSpacing: "-0.02em" }}>
        <span>{item.q}</span>
        <ChevronDown size={20} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
      </button>
      {open && <div style={{ marginTop: 12, color: "var(--ink-3)", fontSize: "1rem", lineHeight: 1.6 }} dangerouslySetInnerHTML={htmlSafe(item.a)} />}
    </div>
  );
};

const FAQ = ({ d }) => (
  <section className="sec">
    <div className="sec-inner" style={{ maxWidth: 820 }}>
      <div className="sec-head">
        {d.eyebrow && <span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span>}
        {d.title && <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(d.title)} />}
      </div>
      <div>{(d.items || []).map((it, i) => <FAQItem key={i} item={it} />)}</div>
    </div>
  </section>
);

const LogoGrid = ({ d }) => (
  <section className="sec">
    <div className="sec-inner">
      <div className="sec-head">
        {d.eyebrow && <span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span>}
        {d.title && <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(d.title)} />}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 16 }}>
        {(d.logos || []).map((l, i) => (
          <div key={i} style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 20, padding: 28, aspectRatio: "3/2", display: "flex", alignItems: "center", justifyContent: "center", filter: "grayscale(0.3)" }}>
            {l.logo_url ? <img src={resolveUrl(l.logo_url)} alt={l.name} style={{ maxWidth: "100%", maxHeight: 64, objectFit: "contain" }} /> : <span style={{ fontFamily: "Clash Display", fontWeight: 600 }}>{l.name}</span>}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Timeline = ({ d }) => (
  <section className="sec">
    <div className="sec-inner">
      <div className="sec-head">
        {d.eyebrow && <span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span>}
        {d.title && <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(d.title)} />}
      </div>
      <div className="timeline-v2">
        <div className="timeline-v2-line" />
        {(d.milestones || []).map((m, idx) => (
          <div key={idx} className={`timeline-v2-row ${idx % 2 === 0 ? "left" : "right"}`}>
            <div className="timeline-v2-card">
              <div className="timeline-v2-icon"><DynIcon name={m.icon || "Rocket"} size={18} /></div>
              <span className="timeline-v2-year">{m.year}</span>
              <h4 className="timeline-v2-title">{m.title}</h4>
              <p className="timeline-v2-desc">{m.description}</p>
            </div>
            <div className="timeline-v2-node" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = ({ d }) => (
  <section className="sec sec-dark">
    <div className="sec-inner">
      <div className="sec-head">
        {d.eyebrow && <span className="eyebrow eyebrow-light"><span className="eyebrow-line eyebrow-line-light" />{d.eyebrow}</span>}
        {d.title && <h2 className="sec-title sec-title-light" dangerouslySetInnerHTML={htmlSafe(d.title)} />}
      </div>
      <div className="testi-grid">
        {(d.items || []).map((t, i) => (
          <div key={i} className="testi">
            <div className="testi-stars">{[...Array(t.rating || 5)].map((_, i) => <Star key={i} size={14} fill="#4ADE80" stroke="#4ADE80" />)}</div>
            <p className="testi-quote">"{t.quote}"</p>
            <div className="testi-foot">
              <div className="testi-avatar">{t.avatar_initial || t.name?.charAt(0) || "·"}</div>
              <div>
                <div className="testi-name">{t.name}</div>
                <div className="testi-org">{t.org}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Pricing = ({ d }) => (
  <section className="sec">
    <div className="sec-inner">
      <div className="sec-head">
        {d.eyebrow && <span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span>}
        {d.title && <h2 className="sec-title" dangerouslySetInnerHTML={htmlSafe(d.title)} />}
        {d.description && <p className="sec-desc">{d.description}</p>}
      </div>
      <div className="pricing-v2-grid">
        {(d.tiers || []).map((tr) => (
          <div key={tr.id} className={`price-card ${tr.is_featured ? "price-card-feat" : ""}`}>
            {tr.is_featured && <div className="price-badge">{tr.featured_badge || "Most Popular"}</div>}
            <div className="price-head">
              <span className="price-tier">{tr.name}</span>
              <div className="price-amount">
                <span className="price-cur">{tr.currency || "₹"}</span>
                <span className="price-num">{tr.price}</span>
                <span className="price-per">{tr.period || "/month"}</span>
              </div>
              <p className="price-desc">{tr.description}</p>
            </div>
            <ul className="price-feats">{(tr.features || []).map((f, i) => <li key={i}><CheckCircle2 size={18} strokeWidth={2} /> {f}</li>)}</ul>
            <button onClick={() => scrollTo(tr.cta_href || "/#contact")} className={tr.is_featured ? "btn-pill-primary btn-pill-full" : "btn-pill-ghost btn-pill-full"}>{tr.cta_label || "Get Started"}</button>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ImageText = ({ d }) => (
  <section className="sec sec-mint">
    <div className="sec-inner">
      <div style={{ display: "grid", gridTemplateColumns: d.image_left ? "1fr 1.2fr" : "1.2fr 1fr", gap: 64, alignItems: "center" }} className="image-text-grid">
        {d.image_left ? (
          <>
            {d.image_url && <img src={resolveUrl(d.image_url)} alt={d.image_alt || ""} style={{ width: "100%", borderRadius: 32, border: "1px solid var(--line)" }} />}
            <div>
              {d.eyebrow && <span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span>}
              {d.title && <h2 className="sec-title" style={{ textAlign: "left", marginTop: 16 }} dangerouslySetInnerHTML={htmlSafe(d.title)} />}
              {d.html && <div style={{ marginTop: 20, fontSize: "1.0625rem", lineHeight: 1.7, color: "var(--ink-3)" }} dangerouslySetInnerHTML={htmlSafe(d.html)} />}
            </div>
          </>
        ) : (
          <>
            <div>
              {d.eyebrow && <span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span>}
              {d.title && <h2 className="sec-title" style={{ textAlign: "left", marginTop: 16 }} dangerouslySetInnerHTML={htmlSafe(d.title)} />}
              {d.html && <div style={{ marginTop: 20, fontSize: "1.0625rem", lineHeight: 1.7, color: "var(--ink-3)" }} dangerouslySetInnerHTML={htmlSafe(d.html)} />}
            </div>
            {d.image_url && <img src={resolveUrl(d.image_url)} alt={d.image_alt || ""} style={{ width: "100%", borderRadius: 32, border: "1px solid var(--line)" }} />}
          </>
        )}
      </div>
    </div>
  </section>
);

const StatsBar = ({ d }) => (
  <section style={{ padding: "60px 24px", textAlign: "center" }}>
    <div className="hero-stats-bar" style={{ display: "inline-flex" }}>
      {(d.stats || []).map((s, i) => (
        <div key={i} style={{ display: "contents" }}>
          <div className="hero-stat"><span className="hero-stat-num">{s.num}</span><span className="hero-stat-label">{s.label}</span></div>
          {i < d.stats.length - 1 && <span className="hero-stat-dot" />}
        </div>
      ))}
    </div>
  </section>
);

const ContactFormBlock = ({ d, onSubmit, submitStatus, isSubmitting, formData, setFormData }) => (
  <section className="sec sec-mint" id="contact">
    <div className="sec-inner">
      <div className="contact-v2">
        <div className="contact-left">
          {d.eyebrow && <span className="eyebrow"><span className="eyebrow-line" />{d.eyebrow}</span>}
          {d.title && <h2 className="sec-title contact-title" dangerouslySetInnerHTML={htmlSafe(d.title)} />}
          {d.description && <p className="contact-text">{d.description}</p>}
        </div>
        <form className="contact-form-v2" onSubmit={onSubmit}>
          {submitStatus === "success" && <div style={{ background: "var(--mint-100)", color: "var(--mint-900)", padding: "14px 18px", borderRadius: 14, marginBottom: 20 }}>✓ Thanks! We'll get back to you within 24 hours.</div>}
          {submitStatus === "error" && <div style={{ background: "#FEE2E2", color: "#991B1B", padding: "14px 18px", borderRadius: 14, marginBottom: 20 }}>Something went wrong. Please try again.</div>}
          <div className="field-row">
            <div className="field"><label>Institute Name</label><input required value={formData.instituteName} onChange={(e) => setFormData({ ...formData, instituteName: e.target.value })} /></div>
            <div className="field"><label>Your Name</label><input required value={formData.yourName} onChange={(e) => setFormData({ ...formData, yourName: e.target.value })} /></div>
          </div>
          <div className="field-row">
            <div className="field"><label>Email</label><input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
            <div className="field"><label>Phone</label><input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
          </div>
          <div className="field"><label>Message</label><textarea rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} /></div>
          <button type="submit" className="btn-pill-primary btn-pill-full btn-pill-lg" disabled={isSubmitting}>{isSubmitting ? "Sending…" : "Send message"} <ArrowRight size={18} strokeWidth={2.5} /></button>
        </form>
      </div>
    </div>
  </section>
);

// Map type → component
export const RENDERERS = {
  hero: Hero,
  feature_grid: FeatureGrid,
  rich_text: RichText,
  cta_banner: CtaBanner,
  faq: FAQ,
  logo_grid: LogoGrid,
  timeline: Timeline,
  testimonials: Testimonials,
  pricing: Pricing,
  image_text: ImageText,
  stats_bar: StatsBar,
  contact_form: ContactFormBlock,
};

const SectionRenderer = ({ section, ...rest }) => {
  if (!section || section.is_visible === false) return null;
  const Cmp = RENDERERS[section.type];
  if (!Cmp) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Unknown section: {section.type}</div>;
  return <Cmp d={section.data || {}} {...rest} />;
};

export default SectionRenderer;
